import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  styled,
} from "@mui/material";
import * as React from "react";
import { useWidth } from "../core/Responsive";
import MaskingStore from "../stores/MaskingStore";
import { getPanelMeasurements, PanelMeasurements } from "../theme";
import { GroupedNavigationProvider } from "./GroupedNavigationContext";

export interface BreakPointColumn {
  columns: number;
  size: "lg" | "md" | "sm" | "xs";
}

interface Props extends MuiDialogProps {
  breakPointColumns?: BreakPointColumn[];
}

const ResponsiveDialog = styled(MuiDialog, {
  shouldForwardProp: (prop) => prop !== "breakPointColumns",
})<Props>(
  ({ theme, fullScreen }) => ({
    "& .MuiDialog-paper": {
      [theme.breakpoints.up("sm")]: {
        maxWidth: theme.dialog.maxWidths.sm,
      },
      [theme.breakpoints.up("md")]: {
        maxWidth: theme.dialog.maxWidths.md,
      },
      [theme.breakpoints.up("lg")]: {
        maxWidth: theme.dialog.maxWidths.lg,
      },
      ...(!fullScreen && {
        [theme.breakpoints.down("sm")]: {
          margin: 16,
        },
      }),
      margin: 0,
    },
  }),
  ({ theme, breakPointColumns }) =>
    breakPointColumns && {
      "& .MuiDialog-paper": {
        ...breakPointColumns.reduce((result, bpc) => {
          const minWidth = 345;
          const measurements: PanelMeasurements = getPanelMeasurements(
            theme,
            bpc.size,
          );

          const columnsWidth = bpc.columns * measurements.columnWidth;
          const guttersWidth = (bpc.columns + 1) * measurements.gutterWidth;
          const width = columnsWidth + guttersWidth;

          result[theme.breakpoints.up(bpc.size)] = {
            width: width < minWidth ? minWidth : width,
          };

          return result;
        }, {}),
      },
    },
);

interface ChildDialog {
  forceClose: () => void;
}

interface ParentDialogInfo {
  addChildDialog: (callback: React.RefObject<ChildDialog>) => void;
  removeChildDialog: (callback: React.RefObject<ChildDialog>) => void;
}

const ParentDialogContext = React.createContext<ParentDialogInfo | null>(null);

export default function Dialog(props: Props): JSX.Element {
  const { breakPointColumns, TransitionProps, ...otherProps } = props;
  const { onEntering, onExited, ...dialogTransitionProps } =
    TransitionProps || {};

  const self = React.useRef<ChildDialog | null>(null);
  const childDialogs = React.useRef<React.RefObject<ChildDialog>[]>([]);
  const isForcedClosed = React.useRef<boolean>(false);
  const isMasked = React.useRef<boolean>(false);

  const parentDialog = React.useContext(ParentDialogContext);
  const width = useWidth();

  const trackClose = React.useCallback(() => {
    if (isMasked.current) {
      MaskingStore.maskClosed();
      isMasked.current = false;
    }
  }, []);

  React.useEffect(() => {
    self.current = {
      forceClose: () => {
        if (!props.open || isForcedClosed.current) {
          return;
        }

        // Ensure any child dialogs are closed before closing the parent dialog so
        // the dialog stack is closed in the correct order (as if each dialog was
        // closed by the user).
        for (const child of childDialogs.current) {
          child.current?.forceClose();
        }

        if (props.onClose) {
          props.onClose({}, "escapeKeyDown");
        }

        trackClose();
        isForcedClosed.current = true;
      },
    };
  }, [props.onClose, props.open]);

  const trackOpen = React.useCallback(() => {
    MaskingStore.maskOpened();
    isMasked.current = true;
  }, []);

  const dialogInfo = React.useMemo<ParentDialogInfo>(
    () => ({
      addChildDialog: (dialog: React.RefObject<ChildDialog>) => {
        childDialogs.current.push(dialog);
      },
      removeChildDialog: (dialog: React.RefObject<ChildDialog>) => {
        childDialogs.current = childDialogs.current.filter((d) => d !== dialog);
      },
    }),
    [],
  );

  React.useEffect(() => {
    return () => {
      if (isMasked.current) {
        MaskingStore.maskClosed();
        isMasked.current = false;
      }
      self.current?.forceClose();
    };
  }, []);

  React.useEffect(() => {
    if (parentDialog) {
      parentDialog.addChildDialog(self);
    }
    return () => {
      if (parentDialog) {
        parentDialog.removeChildDialog(self);
      }
    };
  }, [parentDialog]);

  return (
    // Ensure dialog contents are never controlled by a group because, although
    // dialogs are children of the groups in the react tree, they have their own
    // navigation model once they are opened.
    <GroupedNavigationProvider groupControlsFocus={false}>
      <ParentDialogContext.Provider value={dialogInfo}>
        <ResponsiveDialog
          fullScreen={width === "xs"}
          fullWidth={!!breakPointColumns}
          maxWidth={false}
          TransitionProps={{
            onEntering: (node: HTMLElement, isAppearing: boolean) => {
              trackOpen();
              if (onEntering) {
                onEntering(node, isAppearing);
              }
            },
            onExited: (node: HTMLElement) => {
              trackClose();
              if (onExited) {
                onExited(node);
              }
            },
            ...dialogTransitionProps,
          }}
          {...otherProps}
        />
      </ParentDialogContext.Provider>
    </GroupedNavigationProvider>
  );
}
